// src/pages/Privacy.jsx
import { motion } from 'framer-motion';
import Seo from "../components/Seo";

function Section({ number, title, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900">
        {number}. {title}
      </h2>
      <div className="mt-4 text-gray-700 leading-relaxed space-y-4">
        {children}
      </div>
    </motion.section>
  );
}

function Privacy() {
  return (
    <>
      <Seo
        title="Πολιτική Απορρήτου – ΚΔΒΜ2 ΘΗΣΕΑΣ"
        description="Διαβάστε την πολιτική απορρήτου και προστασίας προσωπικών δεδομένων του ΚΔΒΜ2 ΘΗΣΕΑΣ."
        keywords="πολιτική απορρήτου ΘΗΣΕΑΣ, GDPR, προσωπικά δεδομένα"
        url="https://theseus.edu.gr/privacy"
        image="/theseus.png"
      />
    <div className="min-h-screen bg-gray-50 py-14">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="max-w-5xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
              Πολιτική Προστασίας Προσωπικών Δεδομένων
            </h1>
            <p className="mt-3 text-gray-600">
              Αρ. Πρωτοκόλλου Προστασίας Προσωπικών Δεδομένων: <span className="font-semibold">2511/07-04-2020</span>
            </p>
          </div>

          {/* Quick contact card */}
          <div className="mb-10 rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm">
            <h2 className="text-lg sm:text-xl font-extrabold text-gray-900">
              Στοιχεία Επικοινωνίας (Υπεύθυνος Επεξεργασίας / DPO)
            </h2>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm sm:text-base">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-gray-500 font-semibold">Υπεύθυνος Επεξεργασίας</div>
                <div className="mt-1 font-extrabold text-gray-900">ΚΔΒΜ2 Θησέας</div>
                <div className="mt-3 text-gray-700">
                  <div className="font-semibold text-gray-900">Ταχυδρομική Διεύθυνση</div>
                  Υακίνθου 4, Καβάλα 65403
                </div>
                <div className="mt-3 text-gray-700">
                  <div className="font-semibold text-gray-900">Τηλέφωνο</div>
                  <a
                    href="tel:+30251060020507"
                    className="font-semibold text-gray-900 hover:underline"
                  >
                    +2510 600205-07
                  </a>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="text-gray-500 font-semibold">Υπεύθυνος Προστασίας Δεδομένων (DPO)</div>
                <div className="mt-1 text-gray-700">
                  Διεύθυνση ηλεκτρονικού ταχυδρομείου:{' '}
                  <a href="/contact" className="font-semibold text-gray-900 hover:underline">
                    theseus.kavala@gmail.com
                  </a>
                </div>
                <div className="mt-3 text-gray-700">
                  Για οποιαδήποτε διευκρίνιση σχετικά με την παρούσα πολιτική ή για άσκηση δικαιωμάτων σας,
                  μπορείτε να επικοινωνήσετε μαζί μας.
                </div>
              </div>
            </div>

            <div className="mt-6 text-sm text-gray-600">
              Σε περίπτωση αλλαγής της παρούσας πολιτικής, θα δημοσιοποιήσουμε τις αλλαγές στον ιστότοπό μας.
            </div>
          </div>

          <div className="space-y-8">
            <Section number="1" title="Σχετικά με εμάς">
              <p>
                Καλώς ήλθατε στην Πολιτική Προστασίας Προσωπικών Δεδομένων του ΚΔΒΜ2 Θησέας. Η εταιρία μας σέβεται
                απολύτως τα προσωπικά σας δεδομένα, συμμορφούμενη πλήρως στον Κανονισμό (ΕΕ) 679/2016 (GDPR)
                και την ισχύουσα νομοθεσία.
              </p>
              <p>
                Η παρούσα πολιτική ισχύει τόσο επιγραμμικά όσο και σε φυσικό περιβάλλον και σας ενημερώνει για το
                πώς επεξεργαζόμαστε δεδομένα προσωπικού χαρακτήρα ως επισκέπτη/χρήστη του ιστότοπου, πιθανού
                πελάτη ή πελάτη της εταιρίας, καθώς και για τα δικαιώματά σας.
              </p>
              <p>
                Λαμβάνουμε τα αναγκαία μέτρα ασφαλείας, είμαστε πιστοποιημένοι κατά ISO και έχουμε ορίσει Υπεύθυνο
                Προστασίας Δεδομένων (DPO).
              </p>
            </Section>

            <Section number="2" title="Σε ποιες περιπτώσεις επεξεργαζόμαστε τα δεδομένα σας">
              <p>Επεξεργαζόμαστε τα δεδομένα σας σύμφωνα με την ισχύουσα νομοθεσία και μόνο:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>για εκτέλεση σύμβασης που ετοιμαζόμαστε να συνάψουμε ή έχουμε ήδη συνάψει μαζί σας.</li>
                <li>για συμμόρφωση με έννομη υποχρέωσή μας.</li>
                <li>για τα έννομα συμφέροντά μας (ή τρίτου), εφόσον δεν υπερισχύουν τα δικά σας συμφέροντα/δικαιώματα.</li>
                <li>
                  με ρητή συγκατάθεσή σας (π.χ. ενημερώσεις προωθητικών ενεργειών) ή όταν απαιτείται για ειδικές κατηγορίες δεδομένων.
                </li>
              </ul>
            </Section>

            <Section number="3" title="Πώς συλλέγουμε τα δεδομένα σας">
              <p>
                Συλλέγουμε δεδομένα προσωπικού χαρακτήρα όταν χρησιμοποιείτε τις υπηρεσίες μας (είτε παρέχονται
                απευθείας από εμάς είτε από συνεργάτες που ενεργούν για λογαριασμό μας), όταν χρησιμοποιείτε
                τους ιστοτόπους μας, τηλεφωνικά κέντρα ή εφαρμογές, ή από βάσεις δεδομένων τρίτων όπου έχετε
                παράσχει τα δεδομένα σας.
              </p>
            </Section>

            <Section number="4" title="Για ποιους σκοπούς επεξεργαζόμαστε τα δεδομένα σας">
              <p>
                Παρακάτω παρουσιάζονται ενδεικτικά οι σκοποί επεξεργασίας, οι κατηγορίες δεδομένων και η νομική βάση.
              </p>

              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-left text-gray-700">
                      <th className="px-4 py-3 font-semibold">Σκοπός επεξεργασίας</th>
                      <th className="px-4 py-3 font-semibold">Κατηγορία δεδομένων</th>
                      <th className="px-4 py-3 font-semibold">Νομική βάση</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-gray-700">
                    <tr>
                      <td className="px-4 py-3">Καταχώρηση ως νέου πελάτη</td>
                      <td className="px-4 py-3">Ταυτότητα, Επαφή</td>
                      <td className="px-4 py-3">Σύναψη σύμβασης</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">Επεξεργασία εντολής/παραγγελίας (πληρωμές, είσπραξη οφειλών)</td>
                      <td className="px-4 py-3">Ταυτότητα, Επαφή, Οικονομικά, Συναλλαγή</td>
                      <td className="px-4 py-3">Σύναψη σύμβασης / Έννομο συμφέρον</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">Ενημέρωση για αλλαγές πολιτικής</td>
                      <td className="px-4 py-3">Ταυτότητα, Επαφή</td>
                      <td className="px-4 py-3">Νομική υποχρέωση / Σύμβαση</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">Μέτρα ασφάλειας και προστασίας</td>
                      <td className="px-4 py-3">Ταυτότητα, Επαφή, Τεχνικά</td>
                      <td className="px-4 py-3">Έννομο συμφέρον / Νομική υποχρέωση</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">Ανάλυση δεδομένων για βελτίωση υπηρεσιών/ιστότοπου</td>
                      <td className="px-4 py-3">Τεχνικά, Χρήση</td>
                      <td className="px-4 py-3">Έννομο συμφέρον</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">Εξατομικευμένες προτάσεις (όπου εφαρμόζεται)</td>
                      <td className="px-4 py-3">Ταυτότητα, Επαφή, Χρήση, Προφίλ</td>
                      <td className="px-4 py-3">Συγκατάθεση ή προηγούμενη συναλλαγή</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-sm text-gray-600">
                Σημείωση: Η παραπάνω παρουσίαση είναι συνοπτική/ενδεικτική και εφαρμόζεται ανάλογα με την εκάστοτε δράση/υπηρεσία.
              </p>
            </Section>

            <Section number="5" title="Κατηγορίες δεδομένων που συλλέγουμε">
              <ul className="list-disc pl-6 space-y-2">
                <li><span className="font-semibold">Δεδομένα ταυτότητας:</span> όνομα, επώνυμο, όνομα χρήστη ή παρόμοιο αναγνωριστικό, οικογενειακή κατάσταση, ημερομηνία γέννησης, φύλο.</li>
                <li><span className="font-semibold">Δεδομένα επαφής:</span> email, πόλη, αριθμοί τηλεφώνου.</li>
                <li><span className="font-semibold">Οικονομικά δεδομένα:</span> τραπεζικός λογαριασμός, στοιχεία κάρτας πληρωμής.</li>
                <li><span className="font-semibold">Δεδομένα συναλλαγής:</span> λεπτομέρειες πληρωμών και στοιχεία προϊόντων/υπηρεσιών.</li>
                <li><span className="font-semibold">Τεχνικά δεδομένα:</span> IP, στοιχεία εισόδου, browser, ζώνη ώρας, λειτουργικό, πλατφόρμα.</li>
                <li><span className="font-semibold">Δεδομένα προφίλ:</span> όνομα χρήστη/κωδικός, αγορές/παραγγελίες, ενδιαφέροντα, σχόλια, απαντήσεις σε έρευνες.</li>
                <li><span className="font-semibold">Δεδομένα χρήσης:</span> πληροφορίες για τον τρόπο χρήσης του ιστότοπου/υπηρεσιών.</li>
                <li><span className="font-semibold">Δεδομένα μάρκετινγκ/επικοινωνιών:</span> προτιμήσεις λήψης ενημερώσεων και επικοινωνίας.</li>
                <li><span className="font-semibold">Δεδομένα υγείας / ποινικών καταδικών:</span> μόνο όπου απαιτείται από νομοθεσία ή κατόπιν συγκατάθεσης/αναγκαιότητας για παροχή υπηρεσίας.</li>
              </ul>
            </Section>

            <Section number="6" title="Αλλαγή σκοπού">
              <p>
                Θα χρησιμοποιούμε τα δεδομένα σας μόνο για τους σκοπούς για τους οποίους τα συλλέγουμε.
                Εάν χρειαστεί να τα χρησιμοποιήσουμε για άλλον σκοπό, θα σας ενημερώσουμε ή θα ζητήσουμε
                συγκατάθεση όπου απαιτείται.
              </p>
            </Section>

            <Section number="7" title="Συνδέσεις τρίτων">
              <p>
                Αν ο ιστότοπός μας περιλαμβάνει συνδέσεις προς ιστότοπους τρίτων και ενεργοποιήσετε τις συνδέσεις,
                ενδέχεται τρίτοι να συλλέγουν δεδομένα για εσάς. Δεν ελέγχουμε πολιτικές τρίτων και σας προτρέπουμε
                να ενημερώνεστε πριν τους χρησιμοποιήσετε.
              </p>
            </Section>

            <Section number="8" title="Cookies ή άλλες παρόμοιες τεχνολογίες">
              <p>
                Ο ιστότοπός μας μπορεί να χρησιμοποιεί cookies ή παρόμοιες τεχνολογίες για να σας διακρίνει από άλλους
                επισκέπτες, να καταγράφει τη διεύθυνση IP και τον τρόπο χρήσης του ιστότοπου, ώστε να βελτιώνουμε τις υπηρεσίες μας.
              </p>
              <p className="text-sm text-gray-600">
                Μπορείτε να απενεργοποιήσετε τα cookies από τις ρυθμίσεις του browser σας. Σε αυτή την περίπτωση ενδέχεται να μην είναι διαθέσιμες
                ορισμένες λειτουργίες.
              </p>
            </Section>

            <Section number="9" title="Απόρρητο παιδιών">
              <p>
                Δεν συλλέγουμε εν γνώσει μας πληροφορίες από πρόσωπα κάτω των 15 ετών. Αν είστε κάτω των 15, μην παρέχετε
                πληροφορίες χωρίς συγκατάθεση των ασκούντων τη γονική μέριμνα. Αν διαπιστώσουμε ότι συλλέξαμε δεδομένα παιδιού
                κάτω των 15, θα τα διαγράψουμε.
              </p>
              <p>
                Αν θεωρείτε ότι μπορεί να έχουμε πληροφορίες από ή για παιδί κάτω των 15, παρακαλούμε επικοινωνήστε μαζί μας.
              </p>
            </Section>

            <Section number="10" title="Ασφάλεια δεδομένων">
              <p>
                Έχουμε θέσει σε εφαρμογή οργανωτικά, φυσικά και τεχνολογικά μέτρα προστασίας (όπου απαιτείται, διαδικασίες
                κρυπτογράφησης/ανωνυμοποίησης/ψευδωνυμοποίησης) για την αποτροπή απώλειας, μεταβολής, αποκάλυψης ή μη
                εξουσιοδοτημένης πρόσβασης.
              </p>
              <p>
                Περιορίζουμε την πρόσβαση στα δεδομένα μόνο σε όσους πρέπει να τα γνωρίζουν και εφαρμόζουμε διαδικασίες
                διαχείρισης παραβίασης δεδομένων, ειδοποιώντας όπου απαιτείται από τη νομοθεσία.
              </p>
            </Section>

            <Section number="11" title="Διατήρηση των πληροφοριών σας">
              <p>
                Διατηρούμε δεδομένα προσωπικού χαρακτήρα μόνο όσο απαιτείται για τον σκοπό συλλογής, συμπεριλαμβανομένων
                νομικών/φορολογικών υποχρεώσεων. Σε ορισμένες περιπτώσεις μπορείτε να ζητήσετε διαγραφή, όπου αυτό επιτρέπεται.
              </p>
            </Section>

            <Section number="12" title="Τα δικαιώματά σας">
              <p>Έχετε το δικαίωμα να:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>αιτηθείτε πρόσβαση στα δεδομένα σας προσωπικού χαρακτήρα.</li>
                <li>αιτηθείτε διόρθωση ανακριβών ή ελλιπών δεδομένων.</li>
                <li>αιτηθείτε διαγραφή δεδομένων όπου δεν υπάρχει νόμιμη αιτία συνέχισης επεξεργασίας.</li>
                <li>αντιταχθείτε στην επεξεργασία όταν βασιζόμαστε σε έννομο συμφέρον.</li>
                <li>αιτηθείτε περιορισμό της επεξεργασίας σε συγκεκριμένες περιπτώσεις.</li>
                <li>αιτηθείτε διαβίβαση (φορητότητα) των δεδομένων σας όπου εφαρμόζεται.</li>
                <li>ανακαλέσετε τη συγκατάθεσή σας οποτεδήποτε, χωρίς να θίγεται η προγενέστερη νομιμότητα.</li>
              </ul>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="font-semibold text-gray-900">Για άσκηση δικαιωμάτων</div>
                <div className="mt-2 text-gray-700">
                  Τηλέφωνο:{' '}
                  <a href="tel:+30251060020507" className="font-semibold text-gray-900 hover:underline">
                    +2510 600205-07
                  </a>
                </div>
                <div className="mt-1 text-gray-700">
                  Email:{' '}
                  <a href="/contact" className="font-semibold text-gray-900 hover:underline">
                    theseus.kavala@gmail.com
                  </a>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Επιπλέον, έχετε δικαίωμα επικοινωνίας με την Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα για πιθανές καταγγελίες.
              </p>
            </Section>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
}

export default Privacy;
